import { createContext, useContext } from 'react';

const NO_PROVIDER = Symbol('DeploymentContext.noProvider');

type DeploymentContextValue = {
  /** True when served from an EU-region highlighter host (e.g. `*.eu.letshighlight.com`). */
  isEuHighlighterHost: boolean;
};

const DeploymentContext = createContext<DeploymentContextValue | typeof NO_PROVIDER>(NO_PROVIDER);

interface DeploymentContextProviderProps {
  /** When true, address and related flows may use UK/EU-style fields regardless of stored country. */
  isEuHighlighterHost?: boolean;
  children: React.ReactNode;
}

/**
 * ### DeploymentContextProvider
 *
 * @description
 * Provides deployment-level flags (region, host-derived behavior)
 *
 * @example
 * <DeploymentContextProvider isEuHighlighterHost={isEu}>
 *   <App />
 * </DeploymentContextProvider>
 *
 * @param props - Component props
 * @param props.isEuHighlighterHost - When true, EU-region host was used for this request
 * @param props.children - React node to wrap
 * @returns React component
 */
export const DeploymentContextProvider = ({
  isEuHighlighterHost = false,
  children,
}: DeploymentContextProviderProps) => (
  <DeploymentContext.Provider value={{ isEuHighlighterHost }}>
    {children}
  </DeploymentContext.Provider>
);

/**
 * ### useIsEuHighlighterHost
 *
 * @description
 * Whether the app was loaded from an EU-region highlighter hostname. Set from SSR using the request host.
 *
 * @returns True when the host matches an EU highlighter hostname suffix (production or dev)
 * @throws When used outside DeploymentContextProvider
 */
export const useIsEuHighlighterHost = (): boolean => {
  const context = useContext(DeploymentContext);
  if (context === NO_PROVIDER) {
    throw new Error('useIsEuHighlighterHost must be used within a DeploymentContextProvider');
  }
  return context.isEuHighlighterHost;
};
